import type React from "react";
import {useMemo, useState} from "react";
import {Box, Button, Divider, Skeleton, Stack, Typography} from "@mui/material";
import {useNavigate, useParams} from "react-router";
import AttachIcon from '~/icons/svg/AttachIcon.svg?react'
import type {Supplier360RouteMatch} from "~/routing/catRoutes";
import {useSupplierEntityQuery} from "~/pages/supplier-360/hooks/useSupplierEntityQuery";
import {useComplianceRequirementQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceRequirementQuery";
import {useBuQualificationQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useBuQualificationQuery";
import {useComplianceArtifactsQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceArtifactsQuery";
import {useSupplierBuQualificationsQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useSupplierBuQualificationsQuery";
import {RecordDetailHeader} from "~/pages/supplier-360/tabs/SupplierCompliance/components/recordDetail/RecordDetailHeader";
import {QualificationRail} from "~/pages/supplier-360/tabs/SupplierCompliance/components/recordDetail/QualificationRail";
import {UploadEvidenceForm} from "~/pages/supplier-360/tabs/SupplierCompliance/components/recordDetail/UploadEvidenceForm";
import {EvidenceTable} from "~/pages/supplier-360/tabs/SupplierCompliance/components/evidenceTable/EvidenceTable";
import {ComplianceForm} from "~/pages/supplier-360/tabs/SupplierCompliance/components/ComplianceForm";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";

/** A standalone page: its own padding and scroll, like `SupplierDetailLayout`'s container. */
const PAGE_SX = {p: 2, height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`, overflow: 'auto'};

export const ComplianceRecordDetail: React.FC = () => {
    const {supplierId, recordId} = useParams<Supplier360RouteMatch & {recordId: string}>();
    const {data: entity, isLoading: isEntityLoading, isError: isEntityError} = useSupplierEntityQuery(supplierId);
    const navigate = useNavigate();
    const id = Number(recordId) || undefined;

    const [uploading, setUploading] = useState(false);
    const [editing, setEditing] = useState(false);

    const {data: record, isLoading, isError} = useComplianceRequirementQuery(id);
    const {data: qualification} = useBuQualificationQuery(record?.bu_qualification);
    const {data: artifacts = [], isFetching: artifactsLoading, isError: artifactsError} = useComplianceArtifactsQuery(id);
    // Only the Edit form's "Applies to" options.
    // Only the Edit form's — it shows its own loading / error state for this query.
    const qualificationsQuery = useSupplierBuQualificationsQuery(entity?.id);

    const latestArtifact = useMemo(
        () => [...artifacts].sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))[0],
        [artifacts],
    );

    const goBack = () => navigate('..', {relative: 'path'});

    if (isLoading || isEntityLoading) {
        return <Box sx={PAGE_SX}><Skeleton variant="rectangular" height={480} sx={{borderRadius: 1}}/></Box>;
    }

    if (isError || isEntityError || !record || !entity) {
        return (
            <Stack gap={1} sx={{...PAGE_SX, alignItems: 'center', justifyContent: 'center'}}>
                <Typography color="text.secondary">Could not load this compliance record.</Typography>
                <Button size="small" onClick={goBack} sx={{textTransform: 'none'}}>Back to compliance records</Button>
            </Stack>
        );
    }

    return (
        <Stack gap={3} sx={PAGE_SX}>
            <RecordDetailHeader
                record={record}
                supplierName={entity.canonical_name}
                qualification={qualification}
                onBack={goBack}
                onEdit={() => setEditing(true)}
            />

            <Divider />

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: 'minmax(0, 1fr) 340px'}, gap: 3, alignItems: 'start'}}>
                <Stack gap={1.5}>
                    <Stack direction="row" gap={1.5} sx={{alignItems: 'center'}}>
                        <Typography fontSize={16} fontWeight={600}>{`Evidence ${artifacts.length}`}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {record.layer === 'company_wide'
                                ? 'Held at company level · satisfies this record everywhere'
                                : `Scoped to ${entity.canonical_name} × ${qualification?.business_unit?.bu_name ?? 'this business unit'}`}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AttachIcon width={14} height={14}/>}
                            onClick={() => setUploading(true)}
                            sx={{textTransform: 'none', ml: 'auto'}}
                        >
                            Upload evidence
                        </Button>
                    </Stack>
                    <EvidenceTable
                        artifacts={artifacts}
                        recordState={record.state}
                        loading={artifactsLoading}
                        isError={artifactsError}
                    />
                </Stack>

                <QualificationRail
                    record={record}
                    supplierName={entity.canonical_name}
                    qualification={qualification}
                    latestArtifact={latestArtifact}
                />
            </Box>

            {uploading && (
                <UploadEvidenceForm
                    record={record}
                    businessUnit={qualification?.business_unit?.bu_name}
                    onClose={() => setUploading(false)}
                />
            )}
            {editing && (
                <ComplianceForm
                    entityId={entity.id}
                    qualificationsQuery={qualificationsQuery}
                    record={record}
                    onClose={() => setEditing(false)}
                />
            )}
        </Stack>
    );
};
